import { createTheme, ThemeOptions } from '@mui/material/styles';

const themeConfig = (): ThemeOptions => {
    const mode = 'light';
    const palette = {
        mode: 'light' as const,
        common: { black: '#000000', white: '#ffffff' },
        primary: {
            main: '#e0292a',
            light: '#ff5b5c',
            dark: '#a80000',
            disabled: '#f77066',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#f2711c',
            light: '#f37f33',
            dark: '#da6619',
            disabled: '#6c7b8b',
            contrastText: '#ffffff',
        },
        background: {
            default: '#FFFFFF',
            paper: '#ffffff',
        },
        text: {
            main: '#000000',
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.6)',
            disabled: 'rgba(0, 0, 0, 0.38)',
        },
        error: {
            main: '#d32f2f',
            light: '#ef5350',
            light2: '#f8d7da',
            dark: '#c62828',
            disabled: '#f77066',
            contrastText: '#ffffff',
        },
        warning: {
            main: '#ed6c02',
            light: '#ff9800',
            light2: '#ffcc80',
            dark: '#e65100',
            disabled: '#ffcc80',
            contrastText: '#000000',
        },
        info: {
            main: '#0288d1',
            light: '#03a9f4',
            light2: '#bee5eb',
            dark: '#01579b',
            disabled: '#94d0eb',
            contrastText: '#ffffff',
        },
        success: {
            main: '#2e7d32',
            light: '#4caf50',
            light2: '#c3e6cb',
            dark: '#1b5e20',
            disabled: '#a8cb7f',
            contrastText: '#ffffff',
        },
        grey: {
            50: '#fafafa',
            100: '#f5f5f5',
            200: '#eeeeee',
            300: '#e0e0e0',
            400: '#bdbdbd',
            500: '#9e9e9e',
            600: '#757575',
            700: '#616161',
            800: '#424242',
            900: '#212121',
            A100: '#f5f5f5',
            A200: '#eeeeee',
            A400: '#bdbdbd',
            A700: '#616161',
        },
        contrastThreshold: 3,
        tonalOffset: 0.2,
        divider: 'rgba(0, 0, 0, 0.12)',
        action: {
            active: 'rgba(0, 0, 0, 0.54)',
            hover: 'rgba(0, 0, 0, 0.04)',
            hoverOpacity: 0.04,
            selected: 'rgba(0, 0, 0, 0.08)',
            selectedOpacity: 0.08,
            disabled: 'rgba(0, 0, 0, 0.26)',
            disabledBackground: 'rgba(0, 0, 0, 0.12)',
            disabledOpacity: 0.38,
            focus: 'rgba(0, 0, 0, 0.12)',
            focusOpacity: 0.12,
            activatedOpacity: 0.12,
        },
    };

    const config: ThemeOptions = {
        palette,
        typography: {
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            htmlFontSize: 16,
            fontSize: 14,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 700,
            h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.4 },
            h2: { fontSize: '1.6rem', fontWeight: 500, lineHeight: 1.4 },
            h3: { fontSize: '1.45rem', fontWeight: 500, lineHeight: 1.4 },
            h4: { fontSize: '1.3rem', fontWeight: 500, lineHeight: 1.4 },
            h5: { fontSize: '1.15rem', fontWeight: 400, lineHeight: 1.4 },
            h6: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.4 },
            subtitle1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.4 },
            subtitle2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.4 },
            body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.43,  color: '#212121' },
            body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.4 ,  color: '#757575'},
            button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' as const },
            caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 },
            overline: { fontSize: '0.75rem', fontWeight: 500, textTransform: 'uppercase' as const },
        },
        shape: { borderRadius: 8 },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        borderRadius: 4,
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: '6px 16px',
                    }),
                    startIcon: { marginRight: '8px' },
                    endIcon: { marginLeft: '8px' },
                },
                variants: [
                    { props: { size: 'small' }, style: { fontSize: '0.75rem', padding: '4px 12px' } },
                    { props: { size: 'medium' }, style: { fontSize: '0.875rem', padding: '6px 16px' } },
                    { props: { size: 'large' }, style: { fontSize: '1rem', padding: '8px 22px' } },
                ],
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        // '& .MuiInputBase-input': { fontSize: '0.875rem', padding: '8px 12px' },
                        '& .MuiSelect-select': { fontSize: '0.875rem', padding: '8px 12px' },
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        textDecoration: 'none',
                        color: '#e0292a',
                        '&:hover': { textDecoration: 'underline' },
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#f5f5f5',
                        padding: '8px 24px',
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: { fontSize: '0.75rem' },
                },
            },
        },
        zIndex: {
            mobileStepper: 1000,
            fab: 1050,
            speedDial: 1050,
            appBar: 1100,
            drawer: 1200,
            modal: 1300,
            snackbar: 1400,
            tooltip: 1500,
        },
    };

    return createTheme(config);
};

export default themeConfig;